import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import ValetApi from "../api/Api";
import ValetActivityTooltip from "./ValetActivityTooltip";
import { buildChartDateContextLabel } from "./chartDateContextLabel";
import "./Graph.css";

const BAR_COLORS = ["#93B249", "#0697f7", "#f7b209", "#d32f2f", "#1976d2", "#43a047", "#ffa000", "#6d4c41", "#757575"];

const ValetActivityBarChartsSingle = ({ podiumId, selectedDate = null, startDate = null, endDate = null, userTimeZone = "UTC" }) => {
    const [activityData, setActivityData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dateContextLabel = buildChartDateContextLabel({ selectedDate, startDate, endDate });

    useEffect(() => {
        if (!podiumId) return;
        setLoading(true);
        setError(null);

        const hasDateRange = Boolean(startDate && endDate);
        const request = hasDateRange ? ValetApi.getValetActivityByPodiumIdDateRange(podiumId, userTimeZone, startDate, endDate) : ValetApi.getTodayValetActivityByPodiumId(podiumId, userTimeZone, selectedDate);

        request
            .then((res) => {
                setActivityData(res?.data?.userTransactionCount || []);
                setLoading(false);
            })
            .catch(() => {
                setError("Failed to load data.");
                setActivityData([]);
                setLoading(false);
            });
    }, [podiumId, selectedDate, startDate, endDate, userTimeZone]);

    const chartData = activityData.map((u) => ({
        name: `${u.firstName} ${u.lastName.slice(0, 1)}.`,
        Parked: Number(u.parkedTransactionCount || 0),
        Retrieved: Number(u.retrievedTransactionCount || 0),
        Total: Number(u.totalTransactionCount || 0),
        firstParkTransactionTime: u.firstParkTransactionTime,
        lastParkTransactionTime: u.lastParkTransactionTime,
        firstRetrieveTransactionTime: u.firstRetrieveTransactionTime,
        lastRetrieveTransactionTime: u.lastRetrieveTransactionTime,
        firstTransactionTime: u.firstTransactionTime,
        lastTransactionTime: u.lastTransactionTime,
    }));

    if (!loading && chartData.length === 0) {
        return <div className="valet-activity-no-data">No data available for this chart.</div>;
    }

    return (
        <div className="valet-activity-chart-row">
            <div className="valet-activity-chart-card">
                <h4 className="valet-activity-chart-title">Valet Activity (Bar Chart)</h4>
                <div className="valet-activity-chart-context">Date: {dateContextLabel}</div>
                {error ? (
                    <div className="valet-activity-error">{error}</div>
                ) : (
                    <div className="valet-activity-chart-viewport">
                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                            minWidth={0}
                            debounce={300}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 42, right: 30, left: 10, bottom: 104 }}
                                barCategoryGap="20%">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="name"
                                    interval={0}
                                    minTickGap={0}
                                    tickMargin={12}
                                    height={58}
                                    angle={-18}
                                    textAnchor="end"
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fontSize: 14 }}
                                />
                                <Tooltip content={<ValetActivityTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={28}
                                />
                                <Bar
                                    dataKey="Parked"
                                    fill={BAR_COLORS[0]}
                                    radius={[8, 8, 0, 0]}
                                />
                                <Bar
                                    dataKey="Retrieved"
                                    fill={BAR_COLORS[1]}
                                    radius={[8, 8, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ValetActivityBarChartsSingle;
